import { useState, FormEvent, useEffect } from 'react';
import { X, Send, FileUp, CheckCircle, AlertCircle, Download, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, UpdateCampaignDto } from '../../services/api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

interface Template {
  id: string;
  name: string;
  language: string;
  status: string;
  components: any[];
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: string;
  type: string;
  scheduledAt?: string;
  templateName?: string;
  templateLanguage?: string;
  wabaAccountId?: string;
}

interface CreateCampaignModalProps {
  wabaAccountId: string;
  templates: Template[];
  isOpen: boolean;
  editingCampaign?: Campaign | null;
  qualityRating?: string;
  accountStatus?: string;
  onClose: () => void;
  onSuccess: (campaign: any) => void;
}

interface CSVRow {
  phone: string;
  name?: string;
  [key: string]: string | undefined;
}

interface CSVValidation {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  missingPhone: number;
  invalidPhone: number;
  hasPhoneColumn: boolean;
  columns: string[];
}

type StepType = 'info' | 'template' | 'recipients' | 'review';

export default function CreateCampaignModal({
  wabaAccountId,
  templates,
  isOpen,
  editingCampaign = null,
  qualityRating,
  accountStatus,
  onClose,
  onSuccess,
}: CreateCampaignModalProps) {
  const { accessToken } = useAuth();
  const [currentStep, setCurrentStep] = useState<StepType>('info');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = editingCampaign !== null;

  // Verificar se a qualidade ou conta está bloqueada
  const isQualityBlocked = qualityRating?.toLowerCase() === 'low' || qualityRating?.toLowerCase() === 'red';
  const accStatus = accountStatus?.toUpperCase();
  const isAccountBlocked = accStatus === 'BANNED' || accStatus === 'BLOCKED';
  const isDispatchBlocked = isQualityBlocked || isAccountBlocked;
  const blockMessage = isAccountBlocked
    ? `Conta ${accStatus === 'BANNED' ? 'BANIDA' : 'BLOQUEADA'} — não é possível criar campanhas.`
    : 'Conta com qualidade BAIXA — disparos bloqueados.';

  // Form data
  const [campaignName, setCampaignName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateParams, setTemplateParams] = useState<Record<string, any>>({});
  const [campaignType, setCampaignType] = useState<'BROADCAST' | 'TRANSACTIONAL'>('BROADCAST');
  const [scheduledAt, setScheduledAt] = useState('');
  const [recipientsFile, setRecipientsFile] = useState<File | null>(null);

  // CSV validation
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [csvValidation, setCsvValidation] = useState<CSVValidation | null>(null);
  const [isParsingCSV, setIsParsingCSV] = useState(false);

  // Filtrar apenas templates aprovados (camada extra de segurança)
  const approvedTemplates = templates.filter(
    (t) => t.status === 'APPROVED' || t.status.startsWith('ACTIVE')
  );

  // Resetar ou pré-preencher campos quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      if (editingCampaign) {
        // Modo de edição: pré-preencher campos
        setCampaignName(editingCampaign.name || '');
        setDescription(editingCampaign.description || '');
        setCampaignType(editingCampaign.type as 'BROADCAST' | 'TRANSACTIONAL');
        setScheduledAt(editingCampaign.scheduledAt || '');

        // Se tiver templateName, encontrar o template na lista
        if (editingCampaign.templateName && editingCampaign.templateLanguage) {
          const template = templates.find(
            (t) => t.name === editingCampaign.templateName &&
                   t.language === editingCampaign.templateLanguage
          );
          setSelectedTemplate(template || null);
        }
      } else {
        // Modo de criação: resetar tudo
        resetForm();
      }
    }
  }, [editingCampaign, isOpen, templates]);

  const steps: Array<{ id: StepType; label: string }> = [
    { id: 'info', label: 'Informações' },
    { id: 'template', label: 'Template' },
    { id: 'recipients', label: 'Destinatários' },
    { id: 'review', label: 'Revisar' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  const canProceed = () => {
    switch (currentStep) {
      case 'info':
        return campaignName.trim() !== '';
      case 'template':
        return selectedTemplate !== null;
      case 'recipients':
        // Em modo de edição, arquivo de destinatários é opcional
        if (isEditMode && !recipientsFile) return true;

        // Em modo de criação, arquivo é obrigatório
        if (!recipientsFile) return false;

        // Para CSV, verificar se tem linhas válidas
        if (recipientsFile.name.endsWith('.csv')) {
          return csvValidation !== null && csvValidation.validRows > 0;
        }
        return true; // Excel
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].id);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Aceita números com ou sem + no início
    // Exemplos válidos: +5511999999999, 5511999999999, 11999999999
    const cleaned = phone.trim();

    // Remove o + se existir
    const withoutPlus = cleaned.startsWith('+') ? cleaned.substring(1) : cleaned;

    // Verifica se tem apenas dígitos
    if (!/^\d+$/.test(withoutPlus)) {
      console.log(`[VALIDAÇÃO] ❌ Telefone "${phone}" contém caracteres não numéricos`);
      return false;
    }

    // Deve ter entre 10 e 15 dígitos (padrão internacional)
    // 10-11 dígitos: número brasileiro sem DDI
    // 12-15 dígitos: número com DDI
    const isValid = withoutPlus.length >= 10 && withoutPlus.length <= 15;

    if (!isValid) {
      console.log(`[VALIDAÇÃO] ❌ Telefone "${phone}" tem ${withoutPlus.length} dígitos (esperado: 10-15)`);
    } else {
      console.log(`[VALIDAÇÃO] ✅ Telefone "${phone}" válido (${withoutPlus.length} dígitos)`);
    }

    return isValid;
  };

  const parseCSV = async (file: File): Promise<void> => {
    setIsParsingCSV(true);
    setError(null);

    try {
      const text = await file.text();

      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().toLowerCase(),
        complete: (results) => {
          try {
            const data = results.data as any[];

            if (data.length === 0) {
              throw new Error('O arquivo está vazio ou não possui dados válidos');
            }

            const headers = Object.keys(data[0] || {});

            // Verificar se tem coluna de telefone
            const phoneIndex = headers.findIndex(h =>
              h === 'phone' || h === 'telefone' || h === 'numero' || h === 'number'
            );

            if (phoneIndex === -1) {
              throw new Error('O arquivo precisa ter uma coluna "phone", "telefone" ou "numero"');
            }

            const phoneKey = headers[phoneIndex];
            const nameKey = headers.find(h => h === 'name' || h === 'nome');

            // Parse data rows
            const rows: CSVRow[] = [];
            let validRows = 0;
            let missingPhone = 0;
            let invalidPhone = 0;

            for (const row of data) {
              const phone = row[phoneKey] || '';
              const name = nameKey ? row[nameKey] : undefined;

              const csvRow: CSVRow = { phone };
              if (name) csvRow.name = name;

              // Adicionar outras colunas (variáveis do template)
              headers.forEach((header) => {
                if (header !== phoneKey && header !== nameKey && header) {
                  csvRow[header] = row[header] || '';
                }
              });

              rows.push(csvRow);

              // Validação
              if (!phone) {
                missingPhone++;
              } else if (!validatePhoneNumber(phone)) {
                invalidPhone++;
              } else {
                validRows++;
              }
            }

            const validation: CSVValidation = {
              totalRows: rows.length,
              validRows,
              invalidRows: missingPhone + invalidPhone,
              missingPhone,
              invalidPhone,
              hasPhoneColumn: true,
              columns: headers,
            };

            setCsvData(rows);
            setCsvValidation(validation);

            if (validRows === 0) {
              throw new Error('Nenhum número de telefone válido encontrado no arquivo');
            }

            setIsParsingCSV(false);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao processar arquivo CSV');
            setRecipientsFile(null);
            setCsvData([]);
            setCsvValidation(null);
            setIsParsingCSV(false);
          }
        },
        error: (error) => {
          setError(`Erro ao fazer parse do CSV: ${error.message}`);
          setRecipientsFile(null);
          setCsvData([]);
          setCsvValidation(null);
          setIsParsingCSV(false);
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar arquivo CSV');
      setRecipientsFile(null);
      setCsvData([]);
      setCsvValidation(null);
      setIsParsingCSV(false);
    }
  };

  const parseExcel = async (file: File): Promise<void> => {
    setIsParsingCSV(true);
    setError(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });

      // Usar a primeira planilha
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Converter para JSON
      const data = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        defval: '',
      }) as any[];

      if (data.length === 0) {
        throw new Error('A planilha está vazia ou não possui dados válidos');
      }

      // Normalizar headers para lowercase
      const normalizedData = data.map((row) => {
        const normalizedRow: Record<string, any> = {};
        for (const [key, value] of Object.entries(row)) {
          normalizedRow[key.trim().toLowerCase()] = value;
        }
        return normalizedRow;
      });

      const headers = Object.keys(normalizedData[0] || {});

      console.log('[XLSX] Headers encontrados:', headers);
      console.log('[XLSX] Primeira linha de dados:', normalizedData[0]);
      console.log('[XLSX] Total de linhas:', normalizedData.length);

      // Verificar se tem coluna de telefone
      const phoneKey = headers.find(h =>
        h === 'phone' || h === 'telefone' || h === 'numero' || h === 'number'
      );

      if (!phoneKey) {
        throw new Error('A planilha precisa ter uma coluna "phone", "telefone" ou "numero"');
      }

      const nameKey = headers.find(h => h === 'name' || h === 'nome');

      // Parse data rows
      const rows: CSVRow[] = [];
      let validRows = 0;
      let missingPhone = 0;
      let invalidPhone = 0;

      for (const row of normalizedData) {
        const phone = String(row[phoneKey] || '').trim();
        const name = nameKey ? String(row[nameKey] || '').trim() : undefined;

        const csvRow: CSVRow = { phone };
        if (name) csvRow.name = name;

        // Adicionar outras colunas (variáveis do template)
        headers.forEach((header) => {
          if (header !== phoneKey && header !== nameKey && header) {
            csvRow[header] = String(row[header] || '');
          }
        });

        rows.push(csvRow);

        // Validação
        if (!phone) {
          missingPhone++;
        } else if (!validatePhoneNumber(phone)) {
          invalidPhone++;
        } else {
          validRows++;
        }
      }

      const validation: CSVValidation = {
        totalRows: rows.length,
        validRows,
        invalidRows: missingPhone + invalidPhone,
        missingPhone,
        invalidPhone,
        hasPhoneColumn: true,
        columns: headers,
      };

      setCsvData(rows);
      setCsvValidation(validation);

      if (validRows === 0) {
        throw new Error('Nenhum número de telefone válido encontrado na planilha');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar arquivo Excel');
      setRecipientsFile(null);
      setCsvData([]);
      setCsvValidation(null);
    } finally {
      setIsParsingCSV(false);
    }
  };

  // Extrair variáveis do body do template dinamicamente
  const getBodyVariables = (): string[] => {
    if (!selectedTemplate) {
      // Fallback: se template não foi selecionado, usar variáveis genéricas
      console.warn('Nenhum template selecionado. Usando modelo básico com var1 e var2.');
      return ['var1', 'var2'];
    }

    const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
    if (!bodyComponent?.text) return [];

    const matches = bodyComponent.text.matchAll(/\{\{(\d+)\}\}/g);
    const variables: string[] = [];

    for (const match of matches) {
      const varNum = match[1];
      variables.push(`variavel_${varNum}`);
    }

    return variables.length > 0 ? variables : ['var1', 'var2']; // Fallback se não houver variáveis
  };

  const downloadCSVTemplate = () => {
    // Colunas fixas
    const fixedColumns = ['telefone', 'nome'];

    // Colunas dinâmicas baseadas nas variáveis do template
    const bodyVars = getBodyVariables();
    const allColumns = [...fixedColumns, ...bodyVars];

    // Criar header
    const header = allColumns.join(',');

    // Criar linhas de exemplo (mostrando ambos formatos: com e sem DDI)
    const example1Values = allColumns.map((col, idx) => {
      if (col === 'telefone') return '5511999999999'; // Com DDI
      if (col === 'nome') return 'João Silva';
      return `Valor${idx - 1}`;
    });

    const example2Values = allColumns.map((col, idx) => {
      if (col === 'telefone') return '11888888888'; // Sem DDI (será convertido para +5511888888888)
      if (col === 'nome') return 'Maria Santos';
      return `Valor${idx + 1}`;
    });

    const csvContent = `${header}\n${example1Values.join(',')}\n${example2Values.join(',')}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const fileName = selectedTemplate ? `modelo_${selectedTemplate.name}_destinatarios.csv` : 'modelo_destinatarios.csv';
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadXLSXTemplate = () => {
    // Colunas fixas
    const fixedColumns = ['telefone', 'nome'];

    // Colunas dinâmicas baseadas nas variáveis do template
    const bodyVars = getBodyVariables();
    const allColumns = [...fixedColumns, ...bodyVars];

    // Criar dados (mostrando ambos formatos: com e sem DDI)
    const data = [
      allColumns, // Header
      allColumns.map((col, idx) => {
        if (col === 'telefone') return '5511999999999'; // Com DDI
        if (col === 'nome') return 'João Silva';
        return `Valor${idx - 1}`;
      }),
      allColumns.map((col, idx) => {
        if (col === 'telefone') return '11888888888'; // Sem DDI (será convertido para +5511888888888)
        if (col === 'nome') return 'Maria Santos';
        return `Valor${idx + 1}`;
      }),
    ];

    // Criar workbook e worksheet
    const ws = XLSX.utils.aoa_to_sheet(data);

    // IMPORTANTE: Formatar TODAS as colunas como TEXTO para evitar notação científica
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;

        // Forçar formato de texto
        ws[cellAddress].t = 's'; // 's' = string
        ws[cellAddress].z = '@'; // '@' = formato de texto
      }
    }

    // Definir largura das colunas
    ws['!cols'] = allColumns.map(() => ({ wch: 20 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Destinatários');

    // Fazer download
    const fileName = selectedTemplate ? `modelo_${selectedTemplate.name}_destinatarios.xlsx` : 'modelo_destinatarios.xlsx';
    XLSX.writeFile(wb, fileName, {
      bookType: 'xlsx',
      cellStyles: true,
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const isCSV = file.name.endsWith('.csv') || file.type === 'text/csv';
      const isExcel = file.name.endsWith('.xlsx') || validTypes.includes(file.type);

      if (isCSV) {
        setRecipientsFile(file);
        await parseCSV(file);
      } else if (isExcel) {
        // Processar Excel com xlsx library
        setRecipientsFile(file);
        await parseExcel(file);
      } else {
        setError('Formato de arquivo inválido. Use CSV ou Excel (.xlsx)');
        setRecipientsFile(null);
      }
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedTemplate || !accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      let campaignId: string;
      let campaignResponse;

      if (isEditMode && editingCampaign) {
        // Modo de edição: atualizar campanha existente
        // IMPORTANTE: NÃO enviar campos read-only (templateName, templateLanguage, wabaAccountId, templateParams)
        const updatePayload: UpdateCampaignDto = {
          name: campaignName,
          type: campaignType,
        };

        // Adicionar apenas campos que foram preenchidos
        if (description) {
          updatePayload.description = description;
        }

        if (scheduledAt) {
          updatePayload.scheduledAt = scheduledAt;
        }

        campaignResponse = await apiClient.updateCampaign(
          editingCampaign.id,
          updatePayload,
          accessToken
        );

        if (!campaignResponse.success) {
          throw new Error(campaignResponse.message || 'Erro ao atualizar campanha');
        }

        campaignId = editingCampaign.id;
      } else {
        // Modo de criação: criar nova campanha
        campaignResponse = await apiClient.createCampaign(
          {
            name: campaignName,
            description: description || undefined,
            wabaAccountId,
            templateName: selectedTemplate.name,
            templateLanguage: selectedTemplate.language || 'pt_BR',
            templateParams: Object.keys(templateParams).length > 0 ? templateParams : undefined,
            type: campaignType,
            scheduledAt: scheduledAt || undefined,
          },
          accessToken
        );

        if (!campaignResponse.success) {
          throw new Error(campaignResponse.message || 'Erro ao criar campanha');
        }

        campaignId = campaignResponse.data.id;
      }

      // Step 2: Upload de destinatários usando apiClient (com auto-refresh de token)
      // Só fazer upload se tiver arquivo novo (em edição, arquivo é opcional)
      if (recipientsFile) {
        const recipientsResponse = await apiClient.uploadCampaignRecipients(
          campaignId,
          recipientsFile,
          accessToken
        );

        if (!recipientsResponse.success) {
          throw new Error(recipientsResponse.message || 'Erro ao fazer upload de destinatários');
        }
      }

      // Step 3: Iniciar campanha automaticamente (apenas em modo de criação)
      if (!isEditMode) {
        const startResponse = await apiClient.startCampaign(campaignId, accessToken);

        if (!startResponse.success) {
          throw new Error(startResponse.message || 'Erro ao iniciar campanha');
        }
      }

      // Sucesso!
      onSuccess(campaignResponse.data);

      // Reset form antes de fechar
      resetForm();

      // Fechar modal
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar campanha');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCampaignName('');
    setDescription('');
    setSelectedTemplate(null);
    setTemplateParams({});
    setCampaignType('BROADCAST');
    setScheduledAt('');
    setRecipientsFile(null);
    setCsvData([]);
    setCsvValidation(null);
    setCurrentStep('info');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - Não fecha ao clicar para evitar perda de dados */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-bg-1 border border-border-1 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-bg-1 border-b border-border-1 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-text-1">
              {isEditMode ? 'Editar Campanha' : 'Nova Campanha'}
            </h2>
            <p className="text-text-3 text-sm mt-1">
              {isEditMode
                ? 'Edite as informações da campanha em rascunho'
                : 'Crie e configure uma nova campanha de envio'}
            </p>
            {isEditMode && (
              <p className="text-warning text-xs mt-1">
                ⚠️ Template e conta WABA não podem ser alterados
              </p>
            )}
          </div>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="p-2 hover:bg-bg-3 rounded-md transition-colors"
            disabled={isLoading}
          >
            <X size={20} className="text-text-2" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-6 py-4 border-b border-border-1 bg-bg-2">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      index < currentStepIndex
                        ? 'bg-success text-white'
                        : index === currentStepIndex
                          ? 'bg-accent-1 text-white'
                          : 'bg-bg-3 text-text-3'
                    }`}
                  >
                    {index < currentStepIndex ? <CheckCircle size={16} /> : index + 1}
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium ${
                      index === currentStepIndex ? 'text-text-1' : 'text-text-3'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      index < currentStepIndex ? 'bg-success' : 'bg-bg-3'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-danger/10 border border-danger rounded-md p-4 flex items-center gap-3">
                <AlertCircle className="text-danger" size={20} />
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            {/* Aviso de Conta Bloqueada/Qualidade Baixa */}
            {isDispatchBlocked && (
              <div className="mb-6 bg-danger/10 border border-danger rounded-md p-4 flex items-center gap-3">
                <AlertTriangle className="text-danger flex-shrink-0" size={20} />
                <div>
                  <p className="text-danger font-medium">
                    {blockMessage}
                  </p>
                  <p className="text-danger/80 text-sm">
                    Esta conta está com qualidade BAIXA. Você pode criar a campanha, mas não será possível iniciá-la até a qualidade melhorar.
                  </p>
                </div>
              </div>
            )}

            {/* Step: Info */}
            {currentStep === 'info' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text-1 mb-2">
                    Nome da Campanha <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Ex: Black Friday 2024"
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-md text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-1 mb-2">
                    Descrição (opcional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o objetivo desta campanha"
                    rows={3}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-md text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent resize-none"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-1 mb-2">
                    Agendamento (opcional)
                  </label>
                  <input
                    type="datetime-local"
                    value={
                      scheduledAt
                        ? // Converter ISO para datetime-local no fuso horário de São Paulo
                          new Date(scheduledAt).toLocaleString('sv-SE', {
                            timeZone: 'America/Sao_Paulo',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          }).replace(' ', 'T')
                        : ''
                    }
                    onChange={(e) => {
                      if (!e.target.value) {
                        setScheduledAt('');
                      } else {
                        // Criar data no fuso horário de São Paulo
                        const localDateStr = e.target.value;
                        // Adicionar timezone offset de São Paulo (-03:00)
                        const isoWithTimezone = `${localDateStr}:00-03:00`;
                        setScheduledAt(new Date(isoWithTimezone).toISOString());
                      }
                    }}
                    className="w-full px-4 py-2 bg-bg-3 border border-border-1 rounded-md text-text-3 cursor-not-allowed"
                    disabled={true}
                  />
                  <p className="text-warning text-xs mt-1 font-medium">
                    🚧 Funcionalidade em desenvolvimento - Por enquanto, todas as campanhas são enviadas imediatamente após serem iniciadas
                  </p>
                </div>
              </div>
            )}

            {/* Step: Template */}
            {currentStep === 'template' && (
              <div className="space-y-6">
                {isEditMode ? (
                  // Modo de edição: mostrar template fixo (não editável)
                  <div>
                    <label className="block text-sm font-medium text-text-1 mb-2">
                      Template (não editável)
                    </label>
                    <div className="bg-bg-3 border border-border-1 rounded-md p-4">
                      <p className="text-text-1 font-medium">
                        {editingCampaign?.templateName || selectedTemplate?.name || 'Template não especificado'}
                      </p>
                      <p className="text-text-3 text-sm mt-1">
                        Idioma: {editingCampaign?.templateLanguage || selectedTemplate?.language || 'N/A'}
                      </p>
                      <p className="text-warning text-xs mt-2">
                        ⚠️ O template não pode ser alterado após a criação da campanha
                      </p>
                    </div>
                  </div>
                ) : (
                  // Modo de criação: seletor de template
                  <div>
                    <label className="block text-sm font-medium text-text-1 mb-2">
                      Selecionar Template <span className="text-danger">*</span>
                    </label>
                    <select
                      required
                      value={selectedTemplate?.id || ''}
                      onChange={(e) => {
                        const template = approvedTemplates.find((t) => t.id === e.target.value);
                        setSelectedTemplate(template || null);
                        setTemplateParams({});
                      }}
                      className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-md text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                      disabled={isLoading}
                    >
                      <option value="">Selecione um template aprovado</option>
                      {approvedTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.language})
                        </option>
                      ))}
                    </select>
                    <p className="text-text-3 text-xs mt-1">
                      {approvedTemplates.length} templates aprovados disponíveis
                    </p>
                  </div>
                )}

                {selectedTemplate && (
                  <div className="bg-accent-1/10 border border-accent-1/20 rounded-md p-4">
                    <p className="text-accent-1 text-sm font-medium mb-2">
                      Template Selecionado
                    </p>
                    <p className="text-text-1 font-medium">{selectedTemplate.name}</p>
                    <p className="text-text-3 text-sm">{selectedTemplate.language}</p>
                  </div>
                )}

                <div className="bg-info/10 border border-info/20 rounded-md p-4">
                  <p className="text-accent-1 text-sm">
                    <strong>Dica:</strong> Variáveis globais podem ser configuradas aqui e aplicadas
                    a todos os destinatários. Variáveis específicas podem ser definidas no arquivo CSV.
                  </p>
                </div>
              </div>
            )}

            {/* Step: Recipients */}
            {currentStep === 'recipients' && (
              <div className="space-y-6">
                {isEditMode && (
                  <div className="bg-accent-1/10 border border-accent-1/20 rounded-md p-4">
                    <p className="text-accent-1 text-sm">
                      <strong>Modo de Edição:</strong> Você pode adicionar um novo arquivo de destinatários
                      ou deixar em branco para manter os destinatários existentes.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-text-1 mb-2">
                    Arquivo de Destinatários {!isEditMode && <span className="text-danger">*</span>}
                    {isEditMode && <span className="text-text-3">(opcional em edição)</span>}
                  </label>
                  <div className="border-2 border-dashed border-border-1 rounded-md p-8 text-center hover:border-accent-1 transition-colors bg-bg-2">
                    <FileUp className="mx-auto mb-4 text-text-3" size={48} />
                    {recipientsFile ? (
                      <div>
                        <p className="text-text-1 font-medium mb-2">{recipientsFile.name}</p>
                        <p className="text-text-3 text-sm mb-4">
                          {(recipientsFile.size / 1024).toFixed(2)} KB
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setRecipientsFile(null);
                            setCsvData([]);
                            setCsvValidation(null);
                            setError(null);
                          }}
                          className="text-danger text-sm hover:underline"
                        >
                          Remover arquivo
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-text-1 mb-2">
                          Arraste seu arquivo ou clique para selecionar
                        </p>
                        <p className="text-text-3 text-sm mb-4">
                          Formatos aceitos: CSV, Excel (.xlsx)
                        </p>
                        <input
                          type="file"
                          accept=".csv,.xlsx"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                          disabled={isLoading}
                        />
                        <label
                          htmlFor="file-upload"
                          className="inline-block px-6 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors cursor-pointer"
                        >
                          Selecionar Arquivo
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-bg-1 border border-border-1 rounded-md p-4">
                  <p className="text-text-1 font-medium mb-2">Formato do Arquivo</p>
                  <p className="text-text-2 text-sm mb-2">
                    O arquivo deve conter as seguintes colunas (nesta ordem):
                  </p>
                  <ul className="text-text-2 text-sm list-disc list-inside space-y-1">
                    <li><strong>1ª coluna: telefone</strong> - Número de telefone (veja formatos aceitos abaixo)</li>
                    <li><strong>2ª coluna: nome</strong> (opcional) - Nome do destinatário</li>
                    <li><strong>3ª coluna em diante:</strong> (opcional) - Variáveis do template (variavel_1, variavel_2, etc.)</li>
                  </ul>
                  <div className="mt-3 p-3 bg-info/10 border border-info/20 rounded-lg">
                    <p className="text-accent-1 text-xs font-semibold mb-2">
                      💡 Formatos de telefone aceitos:
                    </p>
                    <ul className="text-accent-1 text-xs space-y-1 ml-4">
                      <li>• <strong>Com DDI:</strong> +5511999999999 ou 5511999999999</li>
                      <li>• <strong>Sem DDI:</strong> 11999999999 (o código +55 será adicionado automaticamente)</li>
                      <li>• Use apenas números (sem espaços, traços ou parênteses)</li>
                    </ul>
                  </div>
                  <div className="mt-3 p-2 bg-accent-1/10 border border-accent-1/20 rounded">
                    <p className="text-accent-1 text-xs mb-2">
                      <strong>💡 Dica:</strong> Baixe um modelo pronto para facilitar:
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={downloadCSVTemplate}
                        className="flex items-center gap-1 px-3 py-1.5 bg-accent-1 hover:bg-accent-2 text-white text-xs rounded transition-colors"
                      >
                        <Download size={14} />
                        Modelo CSV
                      </button>
                      <button
                        type="button"
                        onClick={downloadXLSXTemplate}
                        className="flex items-center gap-1 px-3 py-1.5 bg-accent-1 hover:bg-accent-2 text-white text-xs rounded transition-colors"
                      >
                        <Download size={14} />
                        Modelo XLSX
                      </button>
                    </div>
                  </div>
                </div>

                {/* CSV Parsing Status */}
                {isParsingCSV && (
                  <div className="bg-accent-1/10 border border-accent-1/20 rounded-md p-4">
                    <p className="text-accent-1 text-sm">Processando arquivo...</p>
                  </div>
                )}

                {/* CSV Validation Results */}
                {csvValidation && csvValidation.totalRows > 0 && (
                  <div className="space-y-4">
                    {/* Statistics */}
                    <div className="bg-bg-1 border border-border-1 rounded-md p-4">
                      <p className="text-text-1 font-medium mb-3">Resultado da Validação</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-text-3 text-xs mb-1">Total de Linhas</p>
                          <p className="text-text-1 text-lg font-semibold">{csvValidation.totalRows}</p>
                        </div>
                        <div>
                          <p className="text-text-3 text-xs mb-1">Válidas</p>
                          <p className="text-success text-lg font-semibold">{csvValidation.validRows}</p>
                        </div>
                        <div>
                          <p className="text-text-3 text-xs mb-1">Inválidas</p>
                          <p className="text-danger text-lg font-semibold">{csvValidation.invalidRows}</p>
                        </div>
                      </div>

                      {csvValidation.invalidRows > 0 && (
                        <div className="mt-3 pt-3 border-t border-bg-3">
                          <p className="text-text-2 text-sm mb-2">Problemas encontrados:</p>
                          <ul className="text-text-3 text-xs space-y-1">
                            {csvValidation.missingPhone > 0 && (
                              <li>• {csvValidation.missingPhone} linha(s) sem número de telefone</li>
                            )}
                            {csvValidation.invalidPhone > 0 && (
                              <li>• {csvValidation.invalidPhone} número(s) de telefone inválido(s)</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Preview */}
                    {csvData.length > 0 && (
                      <div className="bg-bg-1 border border-border-1 rounded-md p-4">
                        <p className="text-text-1 font-medium mb-3">Preview (primeiras 5 linhas)</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border-1">
                                <th className="text-left text-text-2 font-medium py-2 px-3">#</th>
                                <th className="text-left text-text-2 font-medium py-2 px-3">Telefone</th>
                                <th className="text-left text-text-2 font-medium py-2 px-3">Nome</th>
                                <th className="text-left text-text-2 font-medium py-2 px-3">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {csvData.slice(0, 5).map((row, index) => {
                                const isValid = row.phone && validatePhoneNumber(row.phone);
                                return (
                                  <tr key={index} className="border-b border-border-1">
                                    <td className="text-text-3 py-2 px-3">{index + 1}</td>
                                    <td className="text-text-1 py-2 px-3">{row.phone || '-'}</td>
                                    <td className="text-text-2 py-2 px-3">{row.name || '-'}</td>
                                    <td className="py-2 px-3">
                                      {isValid ? (
                                        <span className="text-success text-xs">✓ Válido</span>
                                      ) : (
                                        <span className="text-danger text-xs">✗ Inválido</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {csvData.length > 5 && (
                          <p className="text-text-3 text-xs mt-2">
                            ... e mais {csvData.length - 5} linha(s)
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step: Review */}
            {currentStep === 'review' && (
              <div className="space-y-6">
                <div className="bg-bg-1 border border-border-1 rounded-md p-6">
                  <h3 className="text-lg font-semibold text-text-1 mb-4">Revisar Campanha</h3>

                  <div className="space-y-4">
                    <div>
                      <p className="text-text-3 text-sm">Nome</p>
                      <p className="text-text-1 font-medium">{campaignName}</p>
                    </div>

                    {description && (
                      <div>
                        <p className="text-text-3 text-sm">Descrição</p>
                        <p className="text-text-1">{description}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-text-3 text-sm">Template</p>
                      <p className="text-text-1 font-medium">
                        {selectedTemplate?.name} ({selectedTemplate?.language})
                      </p>
                    </div>

                    {scheduledAt && (
                      <div>
                        <p className="text-text-3 text-sm">Agendado para</p>
                        <p className="text-text-1 font-medium">
                          {new Date(scheduledAt).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-text-3 text-sm">Arquivo de Destinatários</p>
                      <p className="text-text-1 font-medium">{recipientsFile?.name}</p>
                    </div>

                    {csvValidation && csvValidation.totalRows > 0 && (
                      <div>
                        <p className="text-text-3 text-sm mb-2">Destinatários Validados</p>
                        <div className="flex gap-4">
                          <div className="bg-success/10 px-3 py-2 rounded">
                            <p className="text-success font-semibold">{csvValidation.validRows}</p>
                            <p className="text-text-3 text-xs">Válidos</p>
                          </div>
                          {csvValidation.invalidRows > 0 && (
                            <div className="bg-danger/10 px-3 py-2 rounded">
                              <p className="text-danger font-semibold">{csvValidation.invalidRows}</p>
                              <p className="text-text-3 text-xs">Inválidos</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {csvValidation && csvValidation.invalidRows > 0 && (
                  <div className="bg-warning/10 border border-warning rounded-md p-4">
                    <p className="text-warning text-sm">
                      <strong>Atenção:</strong> {csvValidation.invalidRows} linha(s) com dados inválidos
                      serão ignoradas no envio. Apenas os {csvValidation.validRows} destinatário(s) válido(s)
                      receberão a mensagem.
                    </p>
                  </div>
                )}

                {!isEditMode && (
                  <div className="bg-info/10 border border-info/20 rounded-md p-4">
                    <p className="text-accent-1 text-sm">
                      <strong>✨ Início Automático:</strong> Sua campanha será criada e iniciada automaticamente.
                      Os envios começarão imediatamente após a confirmação.
                    </p>
                  </div>
                )}

                {isEditMode && (
                  <div className="bg-warning/10 border border-warning/20 rounded-md p-4">
                    <p className="text-warning text-sm">
                      <strong>ℹ️ Modo de Edição:</strong> As alterações serão salvas.
                      Para iniciar o envio, use o botão "Iniciar Campanha" na lista de campanhas.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border-1 px-6 py-4 flex justify-between bg-bg-2">
            <button
              type="button"
              onClick={currentStepIndex === 0 ? () => {
                resetForm();
                onClose();
              } : handleBack}
              disabled={isLoading}
              className="px-6 py-2 bg-bg-3 hover:bg-bg-3/80 text-text-1 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStepIndex === 0 ? 'Cancelar' : 'Voltar'}
            </button>

            {currentStep !== 'review' ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed() || isLoading}
                className="px-6 py-2 bg-accent-1 hover:bg-accent-2 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-2 bg-accent-1 hover:bg-accent-2 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
                {isLoading
                  ? isEditMode
                    ? 'Salvando...'
                    : 'Criando e Iniciando...'
                  : isEditMode
                    ? 'Salvar Alterações'
                    : 'Criar e Iniciar Campanha'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
